import React from 'react';
import { AbsoluteFill } from 'remotion';
import type { FofocaConfig, LayoutRendererProps } from '@/lib/layouts/types';
import { useLayerFont } from '@/hooks/use-layer-font';
import { resolveFillerPlaylist } from '@/lib/layouts/fillerPlaylist';
import { clampFillerSpeed } from '@/lib/layouts/clampFillerSpeed';
import { resolveFofocaStoryMedia } from '@/lib/layouts/resolveFofocaStoryMedia';
import { FillerPlaylist } from './SatisfatorioFrame';

const FONT = 'Arial, Helvetica, sans-serif';

/**
 * A pixel-faithful 1080×1920 social-post composition based on the supplied
 * reference. PostFrameComposition scales this design canvas for smaller
 * portrait exports; the editor already previews post layouts at 1080×1920.
 */
export const FofocaFrame: React.FC<LayoutRendererProps<FofocaConfig>> = ({ config, media, renderClip }) => {
  const caption = (config.caption ?? '').trim();
  const videoTitle = (config.videoTitle ?? '').trim();
  const handle = (config.handle ?? '').trim();
  const badgeBorderRadius = Math.min(50, Math.max(0, config.badgeBorderRadius ?? 50));
  const videoTitleFontFamily = config.videoTitleFontFamily ?? FONT;
  const videoTitleFontSize = Math.min(96, Math.max(20, config.videoTitleFontSize ?? 49));
  const clipOnLeft = config.clipPosition === 'left';
  const useFillerVideo = resolveFofocaStoryMedia(config) === 'video';
  const fillerUrls = useFillerVideo
    ? resolveFillerPlaylist({
      fillerUrl: config.fillerUrl ?? '',
      fillerUrls: config.fillerUrls,
    })
    : [];
  useLayerFont(config.videoTitleFontFamily, config.videoTitleFontUrl);

  return (
    <AbsoluteFill style={{ backgroundColor: '#ffffff', color: '#111111', fontFamily: FONT }}>
      <div
        data-testid="fofoca-post"
        style={{ position: 'absolute', left: 81, top: 504, width: 918 }}
      >
        <div style={{ height: 147, position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
          {config.avatarUrl ? (
            <img
              src={config.avatarUrl}
              alt=""
              style={{ width: 108, height: 108, maxWidth: 'none', maxHeight: 'none', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <div
              data-testid="fofoca-avatar-placeholder"
              style={{
                width: 108,
                height: 108,
                borderRadius: '50%',
                flexShrink: 0,
                background: 'linear-gradient(145deg, #dbc7aa 0%, #8c6848 48%, #34271f 100%)',
              }}
            />
          )}

          <div style={{ paddingLeft: 18, paddingTop: 0, paddingRight: 60, minWidth: 0 }}>
            <div style={{ fontSize: 35, lineHeight: 1.12, fontWeight: 700, letterSpacing: -0.7 }}>
              {config.profileName}
            </div>
            {caption && (
              <div style={{ marginTop: 8, fontSize: 38, lineHeight: 1.16, fontWeight: 400, letterSpacing: -0.65, whiteSpace: 'pre-wrap' }}>
                {caption}
              </div>
            )}
          </div>

          <div
            aria-hidden="true"
            style={{ position: 'absolute', top: 3, right: 8, fontSize: 40, lineHeight: 1, fontWeight: 800, letterSpacing: 3 }}
          >
            ···
          </div>
        </div>

        <div
          data-testid="fofoca-media-row"
          style={{
            position: 'relative',
            width: 918,
            height: 770,
            display: 'flex',
            flexDirection: clipOnLeft ? 'row-reverse' : 'row',
            backgroundColor: '#111111',
            overflow: 'hidden',
          }}
        >
          <div style={{ position: 'relative', width: 457, height: '100%', overflow: 'hidden', backgroundColor: '#202124' }}>
            {fillerUrls.length > 0 ? (
              <FillerPlaylist
                urls={fillerUrls}
                fillerSpeed={clampFillerSpeed(config.fillerSpeed)}
                regionW={457}
                regionH={770}
                fits={config.fillerFits}
                positions={config.fillerPositions}
                slot="story"
              />
            ) : !useFillerVideo && config.imageUrl ? (
              <img
                data-testid="fofoca-static-image"
                src={config.imageUrl}
                alt=""
                style={{ width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none', objectFit: 'cover' }}
              />
            ) : (
              <div
                data-testid="fofoca-story-clip"
                style={{ width: '100%', height: '100%', overflow: 'hidden', backgroundColor: '#000' }}
              >
                {renderClip?.({ muted: true, slot: 'story' })}
              </div>
            )}
          </div>

          <div style={{ width: 6, height: '100%', backgroundColor: '#ffffff', flexShrink: 0 }} />

          <div style={{ position: 'relative', width: 455, height: '100%', overflow: 'hidden', backgroundColor: '#000000' }}>
            {media}
            {!config.headlineAnchor && (videoTitle || handle) && (
              <div data-autocut-over="" style={{ position: 'absolute', left: 22, right: 22, top: 539, textAlign: 'center', textTransform: 'uppercase' }}>
                {videoTitle && (
                  <div
                    style={{
                      color: config.accentColor,
                      fontFamily: videoTitleFontFamily,
                      fontSize: videoTitleFontSize,
                      lineHeight: 1.05,
                      fontWeight: 800,
                      letterSpacing: -1.2,
                      WebkitTextStroke: '3px #111111',
                      paintOrder: 'stroke fill',
                      textShadow: '0 3px 5px rgba(0,0,0,0.8)',
                    }}
                  >
                    {videoTitle}
                  </div>
                )}
                {handle && (
                  <div style={{ marginTop: 9, color: '#9a9a9a', fontSize: 22, lineHeight: 1, fontWeight: 600, textShadow: '0 2px 3px rgba(0,0,0,0.95)' }}>
                    {handle}
                  </div>
                )}
              </div>
            )}
          </div>

          {config.badgeUrl && (
            <img
              data-testid="fofoca-badge"
              data-autocut-over=""
              src={config.badgeUrl}
              alt=""
              style={{
                position: 'absolute',
                left: '50%',
                top: 337,
                width: 148,
                height: 148,
                maxWidth: 'none',
                maxHeight: 'none',
                transform: 'translate(-50%, -50%)',
                objectFit: 'contain',
                borderRadius: `${badgeBorderRadius}%`,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))',
                zIndex: 2,
              }}
            />
          )}
        </div>
      </div>
      {config.headlineAnchor && (videoTitle || handle) && (
        <div
          data-testid="fofoca-title"
          data-autocut-over=""
          style={{
            position: 'absolute',
            left: `${config.headlineAnchor.x * 100}%`,
            top: `${config.headlineAnchor.y * 100}%`,
            transform: 'translate(-50%, -50%)',
            width: '38%',
            textAlign: 'center',
            textTransform: 'uppercase',
            zIndex: 5,
          }}
        >
          {videoTitle && (
            <div
              style={{
                color: config.accentColor,
                fontFamily: videoTitleFontFamily,
                fontSize: videoTitleFontSize,
                lineHeight: 1.05,
                fontWeight: 800,
                letterSpacing: -1.2,
                WebkitTextStroke: '3px #111111',
                paintOrder: 'stroke fill',
                textShadow: '0 3px 5px rgba(0,0,0,0.8)',
              }}
            >
              {videoTitle}
            </div>
          )}
          {handle && (
            <div style={{ marginTop: 9, color: '#9a9a9a', fontSize: 22, lineHeight: 1, fontWeight: 600, textShadow: '0 2px 3px rgba(0,0,0,0.95)' }}>
              {handle}
            </div>
          )}
        </div>
      )}
    </AbsoluteFill>
  );
};
